import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {catchError, map, mergeMap, withLatestFrom} from 'rxjs/operators';
import * as PlayerActions from './player.actions';
import {createInviteSuccess, joinCampaignSuccess, loadMe, loadPlayerSuccess} from './player.actions';
import * as CampaignSelectors from '../campaigns/campaign.selectors';
import {InvitesService, PlayersService} from 'gamesis-story-client';
import {EMPTY, of} from 'rxjs';
import {exitCampaign, setCampaign} from '../campaigns/campaign.actions';
import {isGameMaster} from './player.selectors';

@Injectable()
export class PlayerEffects {
    loadPlayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadMe, setCampaign),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                if (campaign) {
                    return this.playersService.getMyPlayer(campaign)
                        .pipe(
                            map((player) => PlayerActions.loadPlayerSuccess({player})),
                            catchError(error => of(PlayerActions.playerFailure({error})))
                        );
                } else {
                    return of(PlayerActions.playerFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    loadPlayers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlayerActions.loadPlayers, PlayerActions.deletePlayerSuccess, setCampaign, PlayerActions.toggleGMSuccess, PlayerActions.loadMe),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                if (campaign) {
                    return this.playersService.players(campaign).pipe(
                        map((players) => PlayerActions.loadPlayersSuccess({players})),
                        catchError(error => of(PlayerActions.playerFailure({error})))
                    );
                } else {
                    return of(PlayerActions.playerFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    loadInvites$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlayerActions.loadPlayers, PlayerActions.revokeInviteSuccess, createInviteSuccess, loadPlayerSuccess),
            withLatestFrom(this.store.select(CampaignSelectors.selectCurrentCampaignId), this.store.select(isGameMaster)),
            mergeMap(([action, campaign, gameMaster]) => {
                if (!gameMaster) {
                    return EMPTY;
                }
                if (campaign) {
                    return this.invitesService.invites(campaign).pipe(
                        map((invites) => PlayerActions.loadInvitesSuccess({invites})),
                        catchError(error => of(PlayerActions.playerFailure({error})))
                    );
                } else {
                    return of(PlayerActions.playerFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    deletePlayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlayerActions.deletePlayer),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                if (campaign) {
                    return this.playersService.deletePlayer(campaign, action.id)
                        .pipe(
                            map((players) => PlayerActions.deletePlayerSuccess()),
                            catchError(error => of(PlayerActions.playerFailure({error})))
                        );
                } else {
                    return of(PlayerActions.playerFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    revokeInvite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlayerActions.revokeInvite),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                if (campaign) {
                    return this.invitesService.revoke(campaign, action.id)
                        .pipe(
                            map((players) => PlayerActions.revokeInviteSuccess()),
                            catchError(error => of(PlayerActions.playerFailure({error})))
                        );
                } else {
                    return of(PlayerActions.playerFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    createInvite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlayerActions.createInvite),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                if (campaign) {
                    return this.invitesService.invite(campaign)
                        .pipe(
                            map((invite) => PlayerActions.createInviteSuccess({invite})),
                            catchError(error => of(PlayerActions.playerFailure({error})))
                        );
                } else {
                    return of(PlayerActions.playerFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    updateMe$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlayerActions.updateMe),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                if (campaign) {
                    return this.playersService.updateMe(campaign, action.name)
                        .pipe(
                            map((players) => PlayerActions.loadMe()),
                            catchError(error => of(PlayerActions.playerFailure({error})))
                        );
                } else {
                    return of(PlayerActions.playerFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    toggleGM$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlayerActions.toggleGM),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                if (campaign) {
                    return this.playersService.updatePlayer(campaign, action.player.id, {
                        ...action.player,
                        gameMaster: !action.player.gameMaster
                    })
                        .pipe(
                            map((players) => PlayerActions.toggleGMSuccess()),
                            catchError(error => of(PlayerActions.playerFailure({error})))
                        );
                } else {
                    return of(PlayerActions.playerFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    joinCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlayerActions.joinCampaign),
            mergeMap(action => {
                return this.playersService.register(action.code)
                    .pipe(
                        map((campaign) => joinCampaignSuccess({campaign})),
                        catchError(error => of(PlayerActions.playerFailure({error})))
                    );
            })
        )
    );

    joinedCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(joinCampaignSuccess),
            mergeMap((action) => of(setCampaign({id: action.campaign.id})))
        )
    );

    leaveCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlayerActions.leaveCampaign),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                if (campaign) {
                    return this.playersService.leave(campaign)
                        .pipe(
                            map((players) => exitCampaign()),
                            catchError(error => of(PlayerActions.playerFailure({error})))
                        );
                } else {
                    return of(PlayerActions.playerFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private playersService: PlayersService,
        private invitesService: InvitesService
    ) {
    }
}
